import React, { useEffect, useMemo, useState } from 'react';
import {
  AppearanceSelectionDTO,
  appearanceToCharacterDefinition,
  CharacterAppearanceDTO,
  CharacterCustomizationConfig,
  ColorSwatch,
  CustomizationOption,
} from '@hexcrawl/shared-utils';
import { characterService } from '@/ui/services/characterService';
import { ColorSwatchPicker } from './appearance/ColorSwatchPicker';
import { PartIconSelector } from './appearance/PartIconSelector';
import { ToggleWithColor } from './appearance/ToggleWithColor';
import { AppearancePreview } from './appearance/AppearancePreview';

interface Props {
  submitting: boolean;
  onSubmit: (appearance: CharacterAppearanceDTO) => void;
}

function buildDefaultSelections(
  config: CharacterCustomizationConfig,
): Record<string, AppearanceSelectionDTO> {
  const selections: Record<string, AppearanceSelectionDTO> = {};
  for (const opt of config.options) {
    if (opt.inputType === 'colorSwatch' && opt.palette) {
      const palette = config.colorPalettes[opt.palette] ?? [];
      if (palette.length) selections[opt.id] = { type: 'color', hex: palette[0].hex };
    } else if (opt.inputType === 'iconSelector' && opt.choices?.length) {
      selections[opt.id] = { type: 'choice', choiceId: opt.choices[0].id };
    } else if (opt.inputType === 'toggleWithColor') {
      selections[opt.id] = { type: 'toggle', enabled: false };
    }
  }
  return selections;
}

function isVisible(opt: CustomizationOption, selections: Record<string, AppearanceSelectionDTO>): boolean {
  if (!opt.showWhen) return true;
  const ref = selections[opt.showWhen.option];
  if (!ref || ref.type !== 'choice') return false;
  const choiceId = ref.choiceId;
  if (opt.showWhen.not !== undefined && choiceId === opt.showWhen.not) return false;
  if (opt.showWhen.in !== undefined && !opt.showWhen.in.includes(choiceId)) return false;
  if (opt.showWhen.notIn !== undefined && opt.showWhen.notIn.includes(choiceId)) return false;
  return true;
}

export const AppearanceStep: React.FC<Props> = ({ submitting, onSubmit }) => {
  const [config, setConfig] = useState<CharacterCustomizationConfig | null>(null);
  const [selections, setSelections] = useState<Record<string, AppearanceSelectionDTO>>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    characterService.customizationConfig().then((cfg) => {
      if (!mounted) return;
      setConfig(cfg);
      setSelections(buildDefaultSelections(cfg));
    }).catch((e: any) => {
      if (mounted) setLoadError(e?.message || 'Failed to load customization options');
    });
    return () => { mounted = false; };
  }, []);

  const setPart = (optId: string, choiceId: string) =>
    setSelections((prev) => ({ ...prev, [optId]: { type: 'choice', choiceId } }));

  const setColor = (optId: string, hex: string) =>
    setSelections((prev) => ({ ...prev, [optId]: { type: 'color', hex } }));

  const setToggle = (optId: string, enabled: boolean, hex?: string) =>
    setSelections((prev) => ({ ...prev, [optId]: { type: 'toggle', enabled, hex } }));

  const handleSubmit = () => onSubmit({ selections });

  const definition = useMemo(
    () => config ? appearanceToCharacterDefinition({ selections }, config) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(selections), config],
  );

  if (loadError) {
    return <div style={{ color: '#f88', padding: 12 }}>Error: {loadError}</div>;
  }

  if (!config) {
    return <div style={{ color: '#aaa', padding: 12 }}>Loading options…</div>;
  }

  const getPalette = (paletteName: string | undefined): ColorSwatch[] =>
    (paletteName ? config.colorPalettes[paletteName] : undefined) ?? [];

  const renderOption = (opt: CustomizationOption) => {
    if (!isVisible(opt, selections)) return null;

    const sel = selections[opt.id];

    return (
      <div key={opt.id} style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>{opt.label}</div>
        {opt.inputType === 'colorSwatch' && (
          <ColorSwatchPicker
            palette={getPalette(opt.palette)}
            selected={sel?.type === 'color' ? sel.hex : ''}
            onChange={(hex) => setColor(opt.id, hex)}
          />
        )}
        {opt.inputType === 'iconSelector' && opt.choices && (
          <PartIconSelector
            choices={opt.choices}
            selected={sel?.type === 'choice' ? sel.choiceId : ''}
            onChange={(choiceId) => setPart(opt.id, choiceId)}
          />
        )}
        {opt.inputType === 'toggleWithColor' && (
          <ToggleWithColor
            enabled={sel?.type === 'toggle' ? sel.enabled : false}
            hex={sel?.type === 'toggle' ? sel.hex : undefined}
            palette={getPalette(opt.palette)}
            onToggle={() => {
              const cur = sel?.type === 'toggle' ? sel : { type: 'toggle' as const, enabled: false };
              const palette = getPalette(opt.palette);
              setToggle(opt.id, !cur.enabled, cur.hex ?? palette[0]?.hex);
            }}
            onColorChange={(hex) => setToggle(opt.id, true, hex)}
          />
        )}
      </div>
    );
  };

  const canSubmit = !submitting && Object.keys(selections).length > 0;

  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* Left column: preview */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4, flexShrink: 0 }}>
        <AppearancePreview definition={definition} />
        <div style={{ fontSize: 11, color: '#666', textAlign: 'center' }}>Live preview</div>
      </div>

      {/* Right column: options + submit */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 240 }}>
        <div style={{ overflowY: 'auto', maxHeight: 'min(420px, 45vh)', paddingRight: 4 }}>
          {config.options.map(renderOption)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              padding: '6px 14px',
              borderRadius: 4,
              border: 'none',
              background: canSubmit ? '#4CAF50' : '#555',
              color: 'white',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: canSubmit ? 1 : 0.6,
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
